<template>
  <div>
    <b-row align-h="between" align-v="center">
      <b-col sm="12" md="12" :lg="dptmtFuncCol" :xl="dptmtFuncCol">
        <b-form-group
          :label="FormMSG(1, 'Department')"
          :label-cols="`${burgerField ? 0 : 12}`"
          :label-cols-md="`${burgerField ? 0 : 3}`"
          label-for="new-expense_ticket-department"
        >
          <b-input-group>
            <b-form-select
              id="new-expense_ticket-department"
              v-model="selectedDepartment"
              :options="mapDepartments"
              :disabled="isEditable === false"
              :class="{ 'is-invalid': isSubmitted && $v.selectedDepartment.$error }"
              @change="departmentChange"
            />
            <b-input-group-append v-if="addNewOptionEnabled">
              <b-input-group-text class="input-group-text-bg-grams-hair px-1">
                <div class="cursor-pointer">
                  <b-button size="sm" class="btn bg-transparent border-0" @click.prevent="handleAddNewDepartment">
                    <component :is="getLucideIcon('PlusCircle')" color="#00A09C" :size="18" :stroke-width="2.25" />
                  </b-button>
                </div>
              </b-input-group-text>
            </b-input-group-append>
          </b-input-group>
          <div v-if="isSubmitted && $v.selectedDepartment.$error" class="invalid-feedback">
            {{ FormMSG(3, 'Please, choose a department') }}
          </div>
        </b-form-group>
      </b-col>
      <b-col sm="12" md="12" :lg="dptmtFuncCol" :xl="dptmtFuncCol">
        <b-form-group
          :label="FormMSG(2, 'Function')"
          :label-cols="`${burgerField ? 0 : 12}`"
          :label-cols-md="`${burgerField ? 0 : 3}`"
          label-for="new-function"
        >
          <b-input-group>
            <b-form-select
              id="new-function"
              v-model="selectedFunction"
              :options="mapFunctions"
              :disabled="isEditable === false || mapFunctions.length === 0"
              :class="{
                'is-invalid': isSubmitted && $v.selectedFunction.$error,
                'w-75': useMemoDeal && $screen.width > 576 ? true : false,
                'w-100': !useMemoDeal && $screen.width > 576 ? false : true
              }"
              @change="(e) => functionChange(e)"
            />
            <b-input-group-text v-if="addNewOptionEnabled" class="input-group-text-bg-grams-hair d-inline-flex justify-content-center align-items-center px-1">
              <div class="cursor-pointer">
                <b-button size="sm" class="btn bg-transparent border-0" :disabled="!canAddNewFunction" @click.prevent="handleAddNewFunction">
                  <component :is="getLucideIcon('PlusCircle')" color="#00A09C" :size="18" :stroke-width="2.25" />
                </b-button>
              </div>
            </b-input-group-text>
          </b-input-group>
          <div v-if="isSubmitted && $v.selectedFunction.$error" class="invalid-feedback">
            {{ FormMSG(4, 'Please, choose a function') }}
          </div>
        </b-form-group>
      </b-col>
      <b-col v-if="useMemoDeal" sm="12" md="12" lg="3" xl="3">
        <b-button
          :id="`contract-template-${generateKey}`"
          block
          variant="outline-primary"
          :disabled="selectedFunction > 0 && mapFunctions.length === 1"
          @click.prevent.stop="handleLoadContractTemplate"
        >
          <div v-if="isLoadingContractTemplate">{{ FormMSG(150, 'Memo deal already loaded') }}</div>
          <div v-else>{{ FormMSG(151, 'Load memo deal template') }}</div>
        </b-button>
      </b-col>
    </b-row>
    <b-modal
      ref="addNewDepartmentModal"
      v-model="showDepartmentFlowModal"
      no-close-on-backdrop
      no-close-on-esc
      :title="modalTitle"
      size="lg"
      hide-footer
      hide-header-close
    >
      <template #modal-header>
        <h5 class="modal-title">{{ modalTitle }}</h5>
        <div>
          <component
            :is="getLucideIcon('X')"
            color="rgba(6, 38, 62, 0.64)"
            class="cursor-pointer"
            :size="20"
            :stroke-width="3"
            @click="handleCancelAddNewDepartment"
          />
        </div>
      </template>
      <department-form
        v-if="!canAddFunction"
        @department-form:department-created="handleDepartmentCreated"
        @department-form:department-updated="handleDepartmentUpdated"
      />
      <function-form v-if="canAddFunction" ref="function-form" :department="departmentSelected" @function-form:function-updated="handleUpdateFunction" />
    </b-modal>
  </div>
</template>

<script>
// import gql from 'graphql-tag';
import { mapGetters } from 'vuex';
import { store } from '@/store';
import { isNil, greaterThanZero } from '@/shared/utils';
import languageMessages from '@/mixins/languageMessages';
import { validationMixin } from 'vuelidate';
import GlobalMixin from '@/mixins/global.mixin';
import { required, decimal } from 'vuelidate/lib/validators';
import { getFunctions } from '@/cruds/department.crud';
import FunctionForm from '@/views/department/functionForm.vue';
import DepartmentForm from '@/views/department/departmentForm.vue';
import { deleteDepartment } from '@/cruds/department.crud';

export default {
  name: 'DepartmentFunctionSelection',
  components: {
    FunctionForm,
    DepartmentForm
  },
  mixins: [languageMessages, GlobalMixin, validationMixin],
  props: {
    generateKey: {
      type: Number,
      required: false,
      default: 1
    },
    useMemoDeal: {
      type: Boolean,
      required: false,
      default: false
    },
    department: {
      type: Number,
      required: false,
      default: null
    },
    editData: {
      type: Object,
      required: false,
      default: null
    },
    disableEdit: {
      default: false,
      require: false,
      type: Boolean
    },
    burgerField: {
      default: false,
      require: false,
      type: Boolean
    },
    isSubmitted: {
      type: Boolean,
      required: false,
      default: false
    },
    addNewOptionEnabled: {
      type: Boolean,
      required: false,
      default: false
    },
    fromJobRequest: { type: Boolean, default: false, required: false },
    refreshList: { type: Boolean, default: false, required: false }
  },
  data() {
    return {
      selectedDepartment: null,
      selectedFunction: null,
      allFunctions: [],
      elemForm: {
        department: this.department,
        function: 0
      },
      contractTemplateStatus: false,
      isLoadingContractTemplate: false,
      departmentSelected: null,
      functionSelected: null,
      tempFunctionSelected: null,
      tempDepartmentSelected: null,
      showDepartmentFlowModal: false,
      canAddFunction: false,
      functionUpdated: false
    };
  },
  computed: {
    ...mapGetters({
      allDepartements: 'expenses/departmentsForSalary',
      currentJobRequest: 'job-request/currentJobRequest'
    }),
    isEditable() {
      return !this.disableEdit;
    },
    modalTitle() {
      return this.canAddFunction ? this.FormMSG(166, 'Add new function') : this.FormMSG(167, 'Add new department');
    },
    canAddNewFunction() {
      return this.addNewOptionEnabled && this.selectedDepartment !== 0;
    },
    dptmtFuncCol() {
      return this.useMemoDeal ? 4 : 6;
    },
    mapDepartments() {
      const departments = this.allDepartements.map((d) => {
        return {
          value: d.value,
          text: d.message
        };
      });
      // return departments;
      var txt = this.FormMSG(22, 'Select a department');
      return [{ value: 0, text: txt }, ...departments];
      // return [{ value: null, text: "Select a department" }, ...departments];
    },
    mapFunctions() {
      const functions =
        this.allFunctions && this.allFunctions.length > 0
          ? this.allFunctions.map((c) => ({
              value: c.value,
              text: c.message,
              category: c.category
            }))
          : [];
      const txtSelFunc = this.FormMSG(23, 'Select a function');
      const txtWaiting = this.FormMSG(24, 'Select a department first');
      const text = isNil(this.selectedDepartment) ? txtWaiting : txtSelFunc;
      //  ? "Waiting..."
      //  : "Select a Function";
      return [{ value: 0, text }, ...functions];
    }
  },
  watch: {
    refreshList: {
      async handler(newVal) {
        if (newVal === true) {
          await this.initData();

          this.$emit('department-function-selection:init-finish');
        }
      },
      immediate: true,
      deep: true
    },
    isSubmitted: {
      handler(n) {
        if (!isNil(n) && n === true) {
          this.$v.$touch();
          this.$emit('department-function:selector:invalid', this.$v.$invalid);
        }
      },
      deep: true,
      immediate: true
    }
  },
  async mounted() {
    await this.initData();
  },
  methods: {
    handleAddNewDepartment() {
      this.canAddFunction = false;
      this.showDepartmentFlowModal = true;
    },
    handleAddNewFunction() {
      if (this.departmentSelected) {
        this.canAddFunction = true;
        this.showDepartmentFlowModal = true;
      } else {
        this.handleAddNewDepartment();
      }
    },
    handleDepartmentCreated(department) {
      this.departmentSelected = department;
    },
    handleCancelAddNewDepartment() {
      const action = () => {
        if (!this.canAddFunction && this.departmentSelected) {
          deleteDepartment(+this.departmentSelected.id, true).then(() => {
            this.departmentSelected = null;
          });
        }
        this.resetModalState();
      };

      let message = '';
      if (this.departmentSelected && this.$refs['function-form'] && this.$refs['function-form'].functions.length <= 0) {
        message = this.FormMSG(12, 'Are you sure you to cancel ? your changes will be lost');
      } else {
        message = this.FormMSG(38, 'Are you sure to cancel?');
      }

      if (!this.functionUpdated && this.canAddFunction) {
        this.confirmModal(action, message);
      } else {
        action();
      }
    },
    async handleUpdateFunction(value) {
      try {
        this.functionUpdated = true;
        await this.$store.dispatch('expenses/getDepartmentsForSalary');
        this.selectedDepartment = this.tempDepartmentSelected.value;
        await this.departmentChange(this.tempDepartmentSelected.value);

        await this.getFunctions(this.tempDepartmentSelected.value);
        await this.$nextTick();

        this.tempFunctionSelected = value;
        this.selectedFunction = this.tempFunctionSelected.value;

        await this.$nextTick();
        await this.functionChange(this.tempFunctionSelected.value);
      } catch (error) {
        console.error('Error in handleUpdateFunction:', error);
      }
    },
    resetModalState() {
      this.showDepartmentFlowModal = false;
      this.functionUpdated = false;
    },
    async handleDepartmentUpdated(value) {
      this.canAddFunction = true;
      if (typeof value === 'object') {
        this.tempDepartmentSelected = value;
      } else {
        this.tempDepartmentSelected = {
          value: value
        };
      }
      this.departmentSelected = this.tempDepartmentSelected;
    },
    async initData() {
      if (this.fromJobRequest === true) {
        this.$store.dispatch('expenses/getDepartmentsForSalaryJobRequest', +this.currentJobRequest.targetProjectId);
      } else {
        this.$store.dispatch('expenses/getDepartmentsForSalary');
      }
      await this.initSelectedDepartment();
    },
    handleLoadContractTemplate() {
      this.isLoadingContractTemplate = !this.isLoadingContractTemplate;
      this.$emit('active-template:status', this.isLoadingContractTemplate);
      this.isLoadingContractTemplate && this.$emit('department-function-selector:functionSelected', this.elemForm);
    },
    setChangeTemplateStatus(e) {
      this.$emit('active-template:status', e);
    },
    async initSelectedDepartment() {
      if (!isNil(this.editData)) {
        await this.getFunctions(this.editData.department);
        this.selectedDepartment = this.editData.department;
        this.selectedFunction = this.editData.function;
        this.elemForm.function = parseInt(this.editData.function);
        this.elemForm.department = parseInt(this.editData.department);
        //console.log("2 initSelectedDepartment this.editData:",this.editData);
      } else if (!isNil(this.department)) {
        //console.log("3 initSelectedDepartment this.department:",this.department);
        this.selectedDepartment = parseInt(this.department);
        this.departmentChange(this.department);
      } else {
        const { department } = store.state.myProfile;
        this.selectedDepartment = parseInt(department);
        this.departmentChange(department);
        //console.log("4 initSelectedDepartment department from profile:",department);
      }
    },
    async setAutoSelectElement() {
      // console.log("1 setAutoSelectElement this.editData:", this.editData);
      if (!isNil(this.editData.department)) {
        //  console.log("setAutoSelectElement editData function:",this.editData.function);
        //  const { Dep, Fun } = this.editData;
        await this.departmentChange(this.editData.department);
        this.selectedDepartment = this.editData.department;
        this.selectedFunction = this.editData.function;
        //    console.log("setAutoSelectElement this.selectedFunction:",this.selectedFunction);
      }
    },
    // Handle selection changes
    async departmentChange(departementID) {
      if (this.addNewOptionEnabled) {
        this.handleDepartmentUpdated(departementID);
      }
      await this.getFunctions(departementID);
      this.selectedFunction = 0;
      this.elemForm.function = 0; // reset function
      this.elemForm.department = parseInt(departementID);

      await new Promise((resolve, reject) => {
        this.$emit('department-function-selector:departmentSelected', {
          department: this.elemForm.department,
          callback: { resolve, reject }
        });
      });

      if (this.$v) {
        this.$v.$touch();
        this.$emit('department-function:selector:invalid', this.$v.$invalid);
      }
    },
    functionChange(functionID) {
      let funcData = this.allFunctions.find((option) => option.value === functionID);
      this.elemForm.category = funcData ? parseInt(funcData.category, 10) : 0;
      this.elemForm.function = parseInt(functionID);
      this.$emit('department-function-selector:functionSelected', this.elemForm);

      if (this.$v) {
        this.$v.$touch();
        this.$emit('department-function:selector:invalid', this.$v.$invalid);
      }
    },
    async getFunctions(id) {
      this.allFunctions = await getFunctions(id, this.fromJobRequest === true ? +this.currentJobRequest.targetProjectId : null);
    },
    addNewDepartment() {
      this.$emit('add-new-department');
    },
    addNewFunction() {
      this.$emit('add-new-function');
    }
  },
  validations: {
    selectedDepartment: {
      required,
      decimal,
      min: greaterThanZero
    },
    selectedFunction: {
      required,
      decimal,
      min: greaterThanZero
    }
  }
};
</script>
<style lang="scss">
.gap-2 {
  gap: 4px;
}
.btn-custom-ctp {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  border-radius: 8px !important;
  height: calc(1.5em + 0.75rem + 2px);
  font-size: 11px;
}
</style>
